export const detectAvailableCollections = (xmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlContent, 'text/xml');
    const collections = [];

    // Check if root is RuleCollection
    if (doc.documentElement.tagName === 'RuleCollection') {
        const type = doc.documentElement.getAttribute('Type');
        const enforcementMode = doc.documentElement.getAttribute('EnforcementMode');
        if (type) {
            collections.push({
                type: type,
                enforcementMode: enforcementMode || 'Not specified',
                element: doc.documentElement
            });
        }
    } else {
        // Look for RuleCollections within the document
        const ruleCollections = doc.getElementsByTagName('RuleCollection');
        Array.from(ruleCollections).forEach(collection => {
            const type = collection.getAttribute('Type');
            const enforcementMode = collection.getAttribute('EnforcementMode');
            if (type) {
                collections.push({
                    type: type,
                    enforcementMode: enforcementMode || 'Not specified',
                    element: collection
                });
            }
        });
    }

    return collections;
};

export const mergeXmlDocuments = (originalXml, additionalXml, targetCollectionType) => {
    const parser = new DOMParser();
    const serializer = new XMLSerializer();

    // Parse original XML
    const originalDoc = parser.parseFromString(originalXml, 'text/xml');
    const originalErrors = originalDoc.getElementsByTagName('parsererror');

    if (originalErrors.length > 0) {
        throw new Error('Original XML has parsing errors');
    }

    // Prepare additional XML - clean and wrap if necessary
    let cleanedXml = additionalXml.trim();

    // Remove any incomplete RuleCollection tags that might be accidentally included
    cleanedXml = cleanedXml.replace(/<\/?RuleCollection[^>]*>/g, '');

    // If it's not a complete XML document, wrap it
    if (!cleanedXml.startsWith('<?xml')) {
        cleanedXml = `<?xml version="1.0"?><temp>${cleanedXml}</temp>`;
    }

    const additionalDoc = parser.parseFromString(cleanedXml, 'text/xml');
    const additionalErrors = additionalDoc.getElementsByTagName('parsererror');

    if (additionalErrors.length > 0) {
        throw new Error('Additional XML has parsing errors: ' + additionalErrors[0].textContent);
    }

    // Find all individual rules in the additional XML
    const rulesToAdd = {
        FilePublisherRule: [],
        FilePathRule: [],
        FileHashRule: []
    };

    const ruleTypes = ['FilePublisherRule', 'FilePathRule', 'FileHashRule'];

    ruleTypes.forEach(ruleType => {
        const rules = additionalDoc.getElementsByTagName(ruleType);
        Array.from(rules).forEach(rule => {
            rulesToAdd[ruleType].push(rule);
        });
    });

    const totalRules = rulesToAdd.FilePublisherRule.length + rulesToAdd.FilePathRule.length + rulesToAdd.FileHashRule.length;

    if (totalRules === 0) {
        throw new Error('No valid rules found. Please provide FilePublisherRule, FilePathRule, or FileHashRule elements only.');
    }

    // Find target collection based on selected type
    const originalRoot = originalDoc.documentElement;
    let targetCollection = null;

    if (originalRoot.tagName === 'RuleCollection' && originalRoot.getAttribute('Type') === targetCollectionType) {
        // The root element itself is the target RuleCollection
        targetCollection = originalRoot;
    } else if (originalRoot.tagName === 'AppLockerPolicy') {
        // Look for RuleCollections within AppLockerPolicy
        const existingCollections = Array.from(originalRoot.getElementsByTagName('RuleCollection'));
        for (let collection of existingCollections) {
            if (collection.getAttribute('Type') === targetCollectionType) {
                targetCollection = collection;
                break;
            }
        }
    } else {
        // Try to find RuleCollections at any level
        const allCollections = Array.from(originalDoc.getElementsByTagName('RuleCollection'));
        for (let collection of allCollections) {
            if (collection.getAttribute('Type') === targetCollectionType) {
                targetCollection = collection;
                break;
            }
        }
    }

    if (!targetCollection) {
        throw new Error(`No ${targetCollectionType} RuleCollection found in the original XML. The XML must contain a RuleCollection with Type="${targetCollectionType}".`);
    }

    // Group existing rules by type for better insertion
    const existingRules = {
        FilePublisherRule: [],
        FilePathRule: [],
        FileHashRule: []
    };

    // Collect existing rules and their positions
    const allChildren = Array.from(targetCollection.children);
    allChildren.forEach(child => {
        if (ruleTypes.includes(child.tagName)) {
            existingRules[child.tagName].push(child);
        }
    });

    // Add new rules grouped by type
    ruleTypes.forEach(ruleType => {
        if (rulesToAdd[ruleType].length > 0) {
            // Find insertion point - after the last rule of the same type
            let insertionPoint = null;
            if (existingRules[ruleType].length > 0) {
                insertionPoint = existingRules[ruleType][existingRules[ruleType].length - 1];
            }

            // Add each new rule of this type
            rulesToAdd[ruleType].forEach(rule => {
                const importedRule = originalDoc.importNode(rule, true);

                if (insertionPoint && insertionPoint.nextSibling) {
                    targetCollection.insertBefore(importedRule, insertionPoint.nextSibling);
                } else {
                    targetCollection.appendChild(importedRule);
                }

                // Update insertion point for next rule of same type
                insertionPoint = importedRule;
            });
        }
    });

    // Format the XML with proper indentation
    let result = serializer.serializeToString(originalDoc);

    // Clean up any malformed nesting first
    result = result.replace(/<RuleCollection[^>]*>\s*<RuleCollection/g, '<RuleCollection');
    result = result.replace(/<\/RuleCollection>\s*<\/RuleCollection>/g, '</RuleCollection>');

    // Proper XML formatting with consistent indentation
    result = result.replace(/></g, '>\n<'); // Add newlines between all elements

    // Split into lines for proper indentation
    const lines = result.split('\n');
    let indent = 0;
    const indentSize = 2;

    const formattedLines = lines.map(line => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return '';

        // Decrease indent for closing tags
        if (trimmedLine.startsWith('</')) {
            indent = Math.max(0, indent - indentSize);
        }

        const indentedLine = ' '.repeat(indent) + trimmedLine;

        // Increase indent for opening tags (but not self-closing or if followed by closing tag)
        if (trimmedLine.startsWith('<') &&
            !trimmedLine.startsWith('</') &&
            !trimmedLine.endsWith('/>') &&
            !trimmedLine.includes('</')) {
            indent += indentSize;
        }

        return indentedLine;
    });

    result = formattedLines.join('\n');

    // Clean up any double spaces or inconsistencies
    result = result.replace(/\n\s*\n/g, '\n'); // Remove empty lines
    result = result.replace(/^\s+/gm, (match) => {
        // Ensure consistent spacing
        const spaces = Math.floor(match.length / indentSize) * indentSize;
        return ' '.repeat(spaces);
    });

    return result;
};